// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField, useUserInputValue } from '@tectonics/prs/user-input-values';
import { InputMask } from '@tectonics/components/input-mask';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

export function FieldSouthAfricanPhoneNumber(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [selectedEWallet] = useUserInputValue('selectedEWallet');
  const [value, handleChange] = useUserInputAsTextField('southAfricanPhoneNumber');

  const validationError = useUserInputValidationError('southAfricanPhoneNumber');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('telephone');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  const isCapitecPay = selectedEWallet === 'capitec-pay' && selectedPaymentMethod === 'ewallet';

  const fieldLabel = isCapitecPay
    ? 'Phone number (Linked to your Capitec Account)'
    : 'Phone number';

  return (
    <div data-test-name="customer-phone-number">
      <label htmlFor={inputId}>{fieldLabel}</label>

      <TextField
        as={InputMask}
        mask={isCapitecPay ? SOUTH_AFRICA_CAPITEC_MASK : SOUTH_AFRICA_MASK}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        id={inputId}
        placeholder = "Phone number"
        onChange={handleChange}
        readOnly={submitting}
        type="tel"
        value={value}
        name="customer-phone-number"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          Invalid phone number
        </ErrorLabel>
      );
  }
}

const SOUTH_AFRICA_MASK = [
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
];

const SOUTH_AFRICA_CAPITEC_MASK = [
  '0',
  /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/,
];
